import { PsHttpService, type PatSnapCommonResponseData } from '@patsnap/tahiti-core/common/ps-http';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { MaterialFormulationApi } from '../formulation-api';
import type {
  GetToolResultRequest,
  GetToolResultResponse,
  StartSessionRequest,
  StartSessionResponse,
  StopFormulationResponse,
  AgentMessageResponse,
  GetUserTaskResponse,
  FormulationRequest,
  ResumeFormulationRequest,
  UploadUserContextResponse,
  ExtractProcessTableRequest,
  ExtractProcessTableResponse,
  AllSearchSourceItem,
  SupplierSearchRequest,
  SupplierSearchResponse,
} from '@/types/formulation/formulation';
import { FactoryPsHttpSSE } from '@patsnap-utils/sse';
import { useFormulationGuestMode } from '@/composables/formulation/use-guest-mode';

@injectable()
export class MaterialFormulationApiImpl extends PsHttpService implements MaterialFormulationApi {
  @inject(FactoryPsHttpSSE)
  private _factorySsePsHttp!: FactoryPsHttpSSE;

  get options() {
    return {
      baseURL: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/material/formulation-v4`,
      headers: {
        'X-API-Version': '1.0',
      },
    };
  }

  get guestBaseURL() {
    return `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/guest/material/formulation-v4`;
  }

  getToolResult(data: GetToolResultRequest): Promise<GetToolResultResponse> {
    const { isGuestMode } = useFormulationGuestMode();
    const baseURL = isGuestMode.value ? this.guestBaseURL : this.options.baseURL;

    return this.post<PatSnapCommonResponseData<GetToolResultResponse>>({
      baseURL,
      url: '/getToolResult',
      data,
    }).then((res) => {
      return res.data as GetToolResultResponse;
    });
  }

  startSession(request: StartSessionRequest): Promise<StartSessionResponse> {
    const { isGuestMode } = useFormulationGuestMode();
    const baseURL = isGuestMode.value ? this.guestBaseURL : this.options.baseURL;

    return this.post<PatSnapCommonResponseData<StartSessionResponse>>({
      baseURL,
      url: '/formulation/sessions',
      data: request,
    }).then((res) => {
      return res.data as StartSessionResponse;
    });
  }

  stopSession(sessionId: string): Promise<StopFormulationResponse> {
    const { isGuestMode } = useFormulationGuestMode();
    const baseURL = isGuestMode.value ? this.guestBaseURL : this.options.baseURL;

    return this.post<PatSnapCommonResponseData<StopFormulationResponse>>({
      baseURL,
      url: `/formulation/sessions/${sessionId}/stop`,
    }).then((res) => {
      return res.data as StopFormulationResponse;
    });
  }

  listAgentMessages(sessionId: string): Promise<AgentMessageResponse[]> {
    const { isGuestMode } = useFormulationGuestMode();
    const baseURL = isGuestMode.value ? this.guestBaseURL : this.options.baseURL;

    return this.get<PatSnapCommonResponseData<AgentMessageResponse[]>>({
      baseURL,
      url: '/agent-messages/',
      params: { session_id: sessionId },
    }).then((res) => {
      return (res.data ?? []) as AgentMessageResponse[];
    });
  }

  getAgentMessage(agentMessageId: number, sessionId: string): Promise<AgentMessageResponse> {
    const { isGuestMode } = useFormulationGuestMode();
    const baseURL = isGuestMode.value ? this.guestBaseURL : this.options.baseURL;

    return this.get<PatSnapCommonResponseData<AgentMessageResponse>>({
      baseURL,
      url: `/agent-messages/${agentMessageId}`,
      params: { session_id: sessionId },
    }).then((res) => res.data as AgentMessageResponse);
  }

  getLeastSearchDocAgentMessage(
    agentMessageId: number,
    sessionId: string,
  ): Promise<AgentMessageResponse> {
    const { isGuestMode } = useFormulationGuestMode();
    const baseURL = isGuestMode.value ? this.guestBaseURL : this.options.baseURL;

    return this.get<PatSnapCommonResponseData<AgentMessageResponse>>({
      baseURL,
      url: `/agent-messages/leastSearchDoc/${agentMessageId}`,
      params: { session_id: sessionId },
    }).then((res) => res.data as AgentMessageResponse);
  }

  getAllSearchSources(sessionId: string, iteration: number): Promise<AllSearchSourceItem[]> {
    const { isGuestMode } = useFormulationGuestMode();
    const baseURL = isGuestMode.value ? this.guestBaseURL : this.options.baseURL;

    return this.get<PatSnapCommonResponseData<AllSearchSourceItem[]>>({
      baseURL,
      url: '/agent-messages/all-search-sources',
      params: { session_id: sessionId, iteration },
    }).then((res) => {
      return (res.data ?? []) as AllSearchSourceItem[];
    });
  }

  downloadImage(sessionId: string, imageUrl: string): Promise<string> {
    const { isGuestMode } = useFormulationGuestMode();
    const baseURL = isGuestMode.value ? this.guestBaseURL : this.options.baseURL;

    return this.get<PatSnapCommonResponseData<string>>({
      baseURL,
      url: `/formulation/sessions/${sessionId}/image/download`,
      params: { url: imageUrl },
    }).then((res) => (typeof res.data === 'string' ? res.data : ''));
  }

  searchSuppliers(request: SupplierSearchRequest): Promise<SupplierSearchResponse> {
    return this.post<PatSnapCommonResponseData<SupplierSearchResponse>>({
      baseURL: this.options.baseURL,
      url: '/formulation/supplier-search',
      data: request,
    }).then((res) => res.data as SupplierSearchResponse);
  }

  getUserTask(sessionId: string): Promise<GetUserTaskResponse> {
    const { isGuestMode } = useFormulationGuestMode();
    const baseURL = isGuestMode.value ? this.guestBaseURL : this.options.baseURL;

    return this.get<PatSnapCommonResponseData<GetUserTaskResponse>>({
      baseURL,
      url: '/user-tasks/',
      params: { session_id: sessionId },
    }).then((res) => res.data as GetUserTaskResponse);
  }

  formulate(sessionId: string, request: FormulationRequest) {
    const { isGuestMode } = useFormulationGuestMode();
    const baseURL = isGuestMode.value ? this.guestBaseURL : this.options.baseURL;

    return this._factorySsePsHttp<string>()
      .init({
        url: `${baseURL}/formulation/sessions/${sessionId}`,
        data: request,
        hasFinalMessage: () => true,
      })
      .setFetchHeaders(this.options.headers);
  }

  resumeSession(sessionId: string, request: ResumeFormulationRequest) {
    const { isGuestMode } = useFormulationGuestMode();
    const baseURL = isGuestMode.value ? this.guestBaseURL : this.options.baseURL;

    return this._factorySsePsHttp<string>()
      .init({
        url: `${baseURL}/formulation/sessions/${sessionId}/resume`,
        data: request,
        hasFinalMessage: () => true,
      })
      .setFetchHeaders(this.options.headers);
  }

  uploadUserContext(file: File): Promise<UploadUserContextResponse> {
    const { isGuestMode } = useFormulationGuestMode();
    const baseURL = isGuestMode.value ? this.guestBaseURL : this.options.baseURL;

    const formData = new FormData();
    formData.append('file', file);

    return this.post<PatSnapCommonResponseData<UploadUserContextResponse>>({
      baseURL,
      url: '/user-context/upload',
      data: formData,
    }).then((res) => {
      return res.data as UploadUserContextResponse;
    });
  }

  getExtractProcessTable(
    sessionId: string,
    request: ExtractProcessTableRequest,
  ): Promise<ExtractProcessTableResponse> {
    const { isGuestMode } = useFormulationGuestMode();
    const baseURL = isGuestMode.value ? this.guestBaseURL : this.options.baseURL;

    return this.post<PatSnapCommonResponseData<ExtractProcessTableResponse>>({
      baseURL,
      url: `/formulation/sessions/${sessionId}/extract-process-table`,
      data: request,
    }).then((res) => {
      return res.data as ExtractProcessTableResponse;
    });
  }

  handleShare(sessionId: string): Promise<Record<string, unknown>> {
    return this.post<PatSnapCommonResponseData<Record<string, unknown>>>({
      url: '/user-tasks/share',
      params: {
        session_id: sessionId,
      },
    }).then((res) => {
      return res.data as Record<string, unknown>;
    });
  }

  shareEmail(sessionId: string, shareEmail: string): Promise<Record<string, unknown>> {
    return this.post<PatSnapCommonResponseData<Record<string, unknown>>>({
      url: '/user-tasks/share-email',
      data: {
        session_id: sessionId,
        share_email: shareEmail,
      },
    }).then((res) => {
      return res.data as Record<string, unknown>;
    });
  }
}
